import React from "react";
import { Sender } from "../../../types/conversation";
import GetStartedMessage from "./components/GetStartedMessage";
import GoalsMessage from "./components/GoalsMessage";
import { PabSession, User } from "@/services/api";
// import ProceedToAdvisorsMessage from "./components/ProceedToAdvisorsMessage"; // disabled — navigation goes directly after goal confirmation
import { GoalItem } from "@/types/goals";

export interface StepComponentProps {
  onNext: () => void;
  onBack?: () => void;
  selectedGoals?: GoalItem[];
  handleSetSelectedGoal?: (goal: GoalItem, isSelected: boolean) => void;
  handleConfirmMyGoals?: () => void;
  disabled?: boolean;
  onContentLoaded?: () => void;
  pabSession?: PabSession;
  handleProceedToAdvisors?: () => void;
  handleSkipGoals?: () => void;
  user?: User;
}

const StepGetStarted = React.memo(
  ({ onNext, handleSkipGoals, disabled, onContentLoaded, pabSession }: StepComponentProps) => (
    <GetStartedMessage
      handleGetStarted={onNext}
      handleSkip={handleSkipGoals}
      disabled={disabled || !pabSession?.id}
      onContentLoaded={onContentLoaded}
    />
  ),
);

const StepGoals = React.memo(
  ({
    pabSession,
    selectedGoals,
    handleSetSelectedGoal,
    handleConfirmMyGoals,
    disabled,
    onContentLoaded,
  }: StepComponentProps) => (
    <GoalsMessage
      pabSession={pabSession}
      selectedGoals={selectedGoals || []}
      handleSetSelectedGoal={handleSetSelectedGoal!}
      handleConfirmMyGoals={handleConfirmMyGoals!}
      disabled={disabled}
      onContentLoaded={onContentLoaded}
    />
  ),
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    const sessionIdEqual = prevProps.pabSession?.id === nextProps.pabSession?.id;
    const disabledEqual = prevProps.disabled === nextProps.disabled;
    const selectedGoalsEqual =
      JSON.stringify(prevProps.selectedGoals?.sort((a, b) => a.id - b.id)) ===
      JSON.stringify(nextProps.selectedGoals?.sort((a, b) => a.id - b.id));

    return sessionIdEqual && disabledEqual && selectedGoalsEqual;
  },
);

// OKT generation steps disabled — users cannot create OKTs in the onboarding flow.
// Re-enable by uncommenting and restoring the steps array entries below.
/*
const StepSummary = React.memo(
  ({ onNext, selectedGoals, pabSession, disabled, onContentLoaded }: StepComponentProps) => (
    <SummaryMessage
      pabSession={pabSession}
      selectedGoals={selectedGoals || []}
      handleContinue={onNext}
      disabled={disabled}
      onContentLoaded={onContentLoaded}
    />
  ),
  (prevProps, nextProps) => {
    const sessionIdEqual = prevProps.pabSession?.id === nextProps.pabSession?.id;
    const disabledEqual = prevProps.disabled === nextProps.disabled;
    const selectedGoalsEqual =
      JSON.stringify(prevProps.selectedGoals?.sort((a, b) => a.id - b.id)) ===
      JSON.stringify(nextProps.selectedGoals?.sort((a, b) => a.id - b.id));
    return sessionIdEqual && disabledEqual && selectedGoalsEqual;
  },
);

const StepTasks = React.memo(
  ({
    onNext,
    onBack,
    pabSession,
    selectedTasks,
    handleSetSelectedTask,
    handleConfirmMyTasks,
    handleProceedToAdvisors,
    disabled,
    onContentLoaded,
  }: StepComponentProps) => (
    <TasksMessage
      pabSession={pabSession}
      selectedTasks={selectedTasks || []}
      handleSetSelectedTask={handleSetSelectedTask!}
      handleContinue={handleConfirmMyTasks || onNext}
      handleProceedToAdvisors={handleProceedToAdvisors}
      onBack={onBack}
      disabled={disabled}
      onContentLoaded={onContentLoaded}
    />
  ),
  (prevProps, nextProps) => {
    const sessionIdEqual = prevProps.pabSession?.id === nextProps.pabSession?.id;
    const disabledEqual = prevProps.disabled === nextProps.disabled;
    const selectedTasksEqual =
      JSON.stringify(prevProps.selectedTasks?.sort((a, b) => a.id - b.id)) ===
      JSON.stringify(nextProps.selectedTasks?.sort((a, b) => a.id - b.id));
    return sessionIdEqual && disabledEqual && selectedTasksEqual;
  },
);
*/

// StepProceedToAdvisors removed — navigation happens directly after goal confirmation.
// Component kept here for reference/re-enable if needed.
// const StepProceedToAdvisors = React.memo(
//   ({ disabled, handleProceedToAdvisors, onContentLoaded }: StepComponentProps) => (
//     <ProceedToAdvisorsMessage
//       handleProceedToAdvisors={handleProceedToAdvisors}
//       disabled={disabled}
//       onContentLoaded={onContentLoaded}
//     />
//   ),
// );

const createSteps = (user?: User) => {
  // Extract first name from fullName by splitting on space and taking the first part
  const firstName = user?.fullName ? user.fullName.split(" ")[0] : "";

  return [
    {
      messages: [
        {
          sender: Sender.Assistant,
          text: `Hi ${firstName} 👋 Let's craft your 3-5 SMART OKRs for this quarter.`,
          ai: true,
        },
      ],
      Component: StepGetStarted,
    },
    {
      messages: [
        { sender: Sender.User, text: "Get Started", ai: false },
        {
          sender: Sender.Assistant,
          text: "Based on your company's OKRs, team objectives, and your profile, we've generated personalized focus areas for your growth this quarter. \nSelect 3-5 goals that align with your priorities. \nWe strongly encourage you to edit and refine them to make them your own. The more specific they are to your role and context, the more impactful they'll be.",
          ai: true,
        },
      ],
      Component: StepGoals,
    },
    // {
    //   messages: [
    //     {
    //       sender: Sender.Assistant,
    //       text: "Great choices, these goals will shape your growth plan for the quarter.",
    //       ai: true,
    //     },
    //   ],
    //   Component: StepSummary,
    // },
    // {
    //   messages: [
    //     {
    //       sender: Sender.Assistant,
    //       text: "Based on your goals, we've generated starter tasks for you. Refine these to make them actionable and brief - the best tasks are short, specific, and easy to act on.",
    //       ai: true,
    //     },
    //   ],
    //   Component: StepTasks,
    // },
    // StepProceedToAdvisors removed — navigation goes directly after goal confirmation.
    // {
    //   messages: [{ sender: Sender.Assistant, text: "Fantastic! Your growth goals and key tasks are now locked in.\n To help you make meaningful progress, it's time to bring in the right advisors. \n\n You'll have a chance to revisit and refine these tasks during your kickoff session with your advisors.", ai: true }],
    //   Component: StepProceedToAdvisors,
    // },
  ];
};

// Export the function and default steps
export const getSteps = createSteps;
export const steps = createSteps();
